/**
 * Native Bridge Core Logic
 *
 * 네이티브 앱과 웹 간 양방향 통신의 핵심 로직
 */

import { BridgeError, logBridgeError } from './errors'
import { handleNativeEvent } from './events'
import { logPlatformInfo, sendToNative } from './platform'
import type {
  ActionResponse,
  BridgeActions,
  BridgeCallParams,
  BridgeMessage,
  BridgeResponse,
  NativeEventMessage
} from './types'

// UUID 생성 함수
function generateUUID(): string {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID()
  }

  // Fallback for environments without crypto.randomUUID
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8

    return v.toString(16)
  })
}

// Pending requests 관리
interface PendingRequest {
  resolve: (value: any) => void
  reject: (reason: any) => void
  timer?: NodeJS.Timeout // 무제한 타임아웃인 경우 undefined
  group: string
  action: string
  createdAt: number
}

const pendingRequests = new Map<string, PendingRequest>()

// 기본 타임아웃 (30초)
const DEFAULT_TIMEOUT = 30000

// 무제한 타임아웃 (사용자 액션을 기다리는 경우)
const NO_TIMEOUT = 0

/**
 * 액션별 기본 타임아웃 설정
 * - 무제한(0): 사용자 액션을 기다려야 하는 경우 (showAlert, showConfirm)
 * - 기본값(30000): 일반적인 브릿지 호출
 */
const ACTION_TIMEOUTS: Record<string, number> = {
  'Ui:showAlert': NO_TIMEOUT, // 사용자가 버튼을 눌러야 응답
  'Ui:showConfirm': NO_TIMEOUT, // 사용자가 버튼을 눌러야 응답
  'Ui:showCheckboxAlert': NO_TIMEOUT, // 사용자가 버튼을 눌러야 응답
  'Ui:showAdDetailBottomSheet': NO_TIMEOUT // 바텀시트가 닫힐 때까지 대기
  // 필요시 다른 액션들도 여기에 추가 가능
}

/**
 * 네이티브 앱으로 메시지 전송 (타입 안전)
 */

export async function sendMessageToApp<TGroup extends keyof BridgeActions, TAction extends keyof BridgeActions[TGroup]>(
  params: BridgeCallParams<TGroup, TAction>
): Promise<ActionResponse<TGroup, TAction>> {
  const { group, action, data, timeout } = params

  // 타임아웃이 명시되지 않은 경우, 액션별 기본 타임아웃 사용
  const actionKey = `${group}:${action as string}`
  const defaultTimeout = timeout ?? ACTION_TIMEOUTS[actionKey] ?? DEFAULT_TIMEOUT

  // Request ID 생성
  const requestId = generateUUID()

  // 메시지 구성
  const message: BridgeMessage = {
    requestId,
    group: group as string,
    action: action as string,
    data
  }

  // Promise 생성
  const promise = new Promise<ActionResponse<TGroup, TAction>>((resolve, reject) => {
    // 타임아웃 타이머 설정 (무제한인 경우 타이머 설정 안 함)
    const timer =
      defaultTimeout > 0
        ? setTimeout(() => {
            const pending = pendingRequests.get(requestId)
            if (pending) {
              pendingRequests.delete(requestId)
              const timeoutError = BridgeError.timeout(requestId, defaultTimeout)
              logBridgeError(timeoutError, 'sendMessageToApp')
              reject(timeoutError)
            }
          }, defaultTimeout)
        : null

    // Pending request 등록
    pendingRequests.set(requestId, {
      resolve,
      reject,
      timer: timer || undefined, // null인 경우 undefined로 저장
      group: group as string,
      action: action as string,
      createdAt: Date.now()
    })
  })

  try {
    // 네이티브로 메시지 전송
    sendToNative(message)

    return await promise
  } catch (error) {
    // 에러 발생 시 pending request 정리
    const pending = pendingRequests.get(requestId)
    if (pending) {
      if (pending.timer) {
        clearTimeout(pending.timer)
      }
      pendingRequests.delete(requestId)
    }

    throw error
  }
}

/**
 * 네이티브에서 웹으로 오는 메시지 핸들러
 */
export function appMessageHandler(response: BridgeResponse): void {
  try {
    const { requestId, group, action, status, data } = response

    // Pending request 찾기
    const pending = pendingRequests.get(requestId)
    if (!pending) {
      logBridgeError(BridgeError.requestNotFound(requestId), 'appMessageHandler')

      return
    }

    // 타이머 정리
    clearTimeout(pending.timer)
    pendingRequests.delete(requestId)

    // 응답 처리
    if (status === 'SUCCESS') {
      pending.resolve(data)
    } else {
      // status가 ERROR인 경우 reject
      const error = BridgeError.fromNativeError(response)
      logBridgeError(error, 'appMessageHandler')
      pending.reject(error)
    }
  } catch (error) {
    console.error('[Bridge] Error in appMessageHandler:', error)
  }
}

/**
 * 네이티브에서 웹으로 전달되는 이벤트 메시지 핸들러
 * window.handleNativeMessage(messageString) 형태로 호출됨
 *
 * @param messageStringOrObject JSON 문자열 또는 NativeEventMessage 객체
 */
export function handleNativeMessage(messageStringOrObject: string | NativeEventMessage): void {
  try {
    let message: NativeEventMessage

    // 문자열인 경우 JSON 파싱, 객체인 경우 그대로 사용
    if (typeof messageStringOrObject === 'string') {
      try {
        message = JSON.parse(messageStringOrObject)
      } catch (parseError) {
        console.error('[Bridge] Error parsing JSON string:', {
          messageString: messageStringOrObject,
          error: parseError instanceof Error ? parseError.message : 'Unknown error'
        })

        return
      }
    } else if (typeof messageStringOrObject === 'object' && messageStringOrObject !== null) {
      // 이미 객체인 경우
      message = messageStringOrObject
    } else {
      console.error('[Bridge] Invalid message type:', {
        type: typeof messageStringOrObject,
        value: messageStringOrObject
      })

      return
    }

    // 필수 필드 검증
    if (!message.group || !message.action) {
      console.error('[Bridge] Invalid native event message format:', {
        message,
        error: 'Missing required fields: group or action'
      })

      return
    }

    // 이벤트 핸들러로 전달
    handleNativeEvent(message)
  } catch (error) {
    console.error('[Bridge] Error processing native event message:', {
      messageStringOrObject,
      error: error instanceof Error ? error.message : 'Unknown error'
    })
  }
}

/**
 * Window 객체에 메시지 핸들러 등록
 */
export function initializeBridge(): void {
  if (typeof window !== 'undefined') {
    // 웹 → 네이티브 응답 핸들러 (기존)
    window.appMessageHandler = appMessageHandler

    // 네이티브 → 웹 이벤트 핸들러 (신규)
    window.handleNativeMessage = handleNativeMessage

    window.onResume = () => {}

    logPlatformInfo()
  }
}

/**
 * 브릿지 초기화 여부 확인
 */
export function isBridgeInitialized(): boolean {
  return typeof window !== 'undefined' && !!window.appMessageHandler && !!window.handleNativeMessage
}

/**
 * Pending requests 정리 (메모리 관리)
 */
export function cleanupPendingRequests(): void {
  const now = Date.now()
  const expiredRequests: string[] = []

  pendingRequests.forEach((pending, requestId) => {
    // 5분 이상 된 요청은 자동 정리
    if (now - pending.createdAt > 5 * 60 * 1000) {
      expiredRequests.push(requestId)
    }
  })

  expiredRequests.forEach(requestId => {
    const pending = pendingRequests.get(requestId)
    if (pending) {
      clearTimeout(pending.timer)
      pendingRequests.delete(requestId)
      pending.reject(BridgeError.timeout(requestId, 0))
    }
  })

  if (expiredRequests.length > 0) {
    console.warn(`[Bridge] Cleaned up ${expiredRequests.length} expired requests`)
  }
}

/**
 * 현재 pending requests 상태 반환 (디버깅용)
 */
export function getPendingRequestsStatus(): {
  count: number
  requests: Array<{
    requestId: string
    group: string
    action: string
    createdAt: number
    age: number
  }>
} {
  const requests = Array.from(pendingRequests.entries()).map(([requestId, pending]) => ({
    requestId,
    group: pending.group,
    action: pending.action,
    createdAt: pending.createdAt,
    age: Date.now() - pending.createdAt
  }))

  return {
    count: requests.length,
    requests
  }
}

// 자동 초기화 (브라우저 환경에서만)
if (typeof window !== 'undefined') {
  // DOM이 로드된 후 초기화
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeBridge)
  } else {
    initializeBridge()
  }

  // 주기적으로 pending requests 정리 (5분마다)
  setInterval(cleanupPendingRequests, 5 * 60 * 1000)
}
